import { useMemo } from 'react'

interface SliderProps {
  label: string
  sublabel?: string
  value: number
  min: number
  max: number
  step?: number
  unit?: string
  formatValue?: (v: number) => string
  onChange: (v: number) => void
  logScale?: boolean
  icon?: React.ReactNode
}

// Logarithmic mapping for wide-range inputs (storage, egress, invocations)
function toLog(v: number, min: number, max: number): number {
  const lmin = Math.log(Math.max(min, 1))
  const lmax = Math.log(max)
  const lv = Math.log(Math.max(v, 1))
  return ((lv - lmin) / (lmax - lmin)) * 100
}

function fromLog(p: number, min: number, max: number): number {
  const lmin = Math.log(Math.max(min, 1))
  const lmax = Math.log(max)
  const lv = lmin + (p / 100) * (lmax - lmin)
  return Math.exp(lv)
}

function Slider({
  label,
  sublabel,
  value,
  min,
  max,
  step = 1,
  unit,
  formatValue,
  onChange,
  logScale = false,
  icon,
}: SliderProps) {
  const fillPct = useMemo(() => {
    if (logScale) return toLog(value, min, max)
    return ((value - min) / (max - min)) * 100
  }, [value, min, max, logScale])

  const display = formatValue ? formatValue(value) : `${value}${unit ? ` ${unit}` : ''}`

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = Number(e.target.value)
    if (logScale) {
      const v = fromLog(raw, min, max)
      // snap to step
      const snapped = Math.round(v / step) * step
      onChange(Math.max(min, Math.min(max, snapped)))
    } else {
      onChange(raw)
    }
  }

  return (
    <div className="group">
      <div className="flex items-end justify-between mb-2.5">
        <div className="flex items-center gap-2">
          {icon && <span className="text-cyan-400/80">{icon}</span>}
          <div>
            <div className="text-[13px] font-semibold text-slate-200 tracking-tight">{label}</div>
            {sublabel && <div className="text-[11px] text-slate-500 mt-0.5">{sublabel}</div>}
          </div>
        </div>
        <div className="font-mono text-[15px] font-semibold text-cyan-300 tabular-nums">
          {display}
        </div>
      </div>
      <input
        type="range"
        className="cc-slider w-full"
        min={logScale ? 0 : min}
        max={logScale ? 100 : max}
        step={logScale ? 0.1 : step}
        value={logScale ? fillPct : value}
        onChange={handleChange}
        style={{ ['--fill' as any]: `${fillPct}%` }}
      />
      <div className="flex justify-between mt-1.5 font-mono text-[10px] text-slate-600 tabular-nums">
        <span>{formatValue ? formatValue(min) : min}</span>
        <span>{formatValue ? formatValue(max) : max}</span>
      </div>
    </div>
  )
}

export default Slider
